import React, { useState } from 'react';
import { Box, Button, Typography, useTheme } from '@mui/material';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import {
    Layout,
    LAYOUT_DOCUMENTS_1_3,
    LAYOUT_DOCUMENTS_FULL,
} from '@europrocurement/l2d-modules/modules/Dossiers/constants/layouts';
import useFileChecker from '@europrocurement/l2d-modules/modules/Dossiers/hooks/useFileChecker';
import { useSnackbar } from 'notistack';
import useDossierFile from '@europrocurement/l2d-modules/modules/Dossiers/hooks/useDossierFile';
import { useDossierDocumentsCtx } from '@europrocurement/l2d-modules/providers';
import { useDossierDocumentFilesApi } from '@europrocurement/l2d-modules/modules/Dossiers/hooks';
import DossierDocumentsValidationHeader from '../DossierDocumentsValidationHeader/DossierDocumentsValidationHeader';
import DossierDocumentsValidationList from '../DossierDocumentsValidationList/DossierDocumentsValidationList';
import DocumentViewer from '../../DocumentViewer/DocumentViewer';

export type DossierDocumentsValidationProps = {
    currentLayout: Layout;
};

const DossierDocumentsValidation: React.FunctionComponent<DossierDocumentsValidationProps> =
    function (props) {
        const { currentLayout } = props;
        const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
        const [listDisplayed, setListDisplayed] = useState<boolean>(true);
        const { handleUpdateDosDocFile } = useDossierFile({ setIsSubmitting });
        const { displayFilesCheckerForm } = useFileChecker();
        const { enqueueSnackbar } = useSnackbar();
        const { palette } = useTheme();
        const { refreshDossierDocumentsContext } = useDossierDocumentsCtx();
        const { getDosDocFileContent } = useDossierDocumentFilesApi();

        const wrapperSx = {
            overflow: 'hidden',
            backgroundColor: palette.background.paper,
            borderRadius: '12px',
            borderColor: palette.grey[200],
            borderWidth: '1px',
            borderStyle: 'solid',
            boxShadow: '0px 2px 2.5px 0px rgba(193, 202, 215, 0.25)',
        };

        const handleChangeDocumentType = (fichierId: number, newTypeDocId: number) => {
            if (isSubmitting) {
                return;
            }
            handleUpdateDosDocFile(fichierId as number, { typeDocDossier: newTypeDocId }, true);
        };

        const onDocumentStatusChange = (fichierId: number, statusId: number) => {
            if (isSubmitting) {
                return;
            }
            handleUpdateDosDocFile(fichierId as number, { status: statusId });
        };

        const navigationBtn = (
            <Button
                sx={{
                    display: 'flex',
                    gap: '0 8px',
                    alignItems: 'center',
                    color: palette.secondary.main,
                }}
                onClick={() => setListDisplayed((v) => !v)}
            >
                <FaOptionIcon
                    sx={{ lineHeight: 0, display: 'flex', alignItems: 'center', fontSize: '14px' }}
                    icon={
                        listDisplayed
                            ? ['fasl', 'left-from-bracket']
                            : ['fasl', 'right-from-bracket']
                    }
                />
                <Typography
                    sx={{ fontSize: '14px' }}
                    variant="caption"
                >
                    {listDisplayed ? 'Masquer' : 'Afficher'}
                </Typography>
            </Button>
        );

        const handleDeliassage = async (fichierId: number) => {
            const { files } = await handleUpdateDosDocFile(fichierId as number, {
                typageAuto: true,
                deliassageAuto: true,
            });
            if (files && files.length > 1) {
                const filteredFiles = files.filter((f) => f.id !== fichierId);
                if (filteredFiles.length > 0) {
                    // files checker modal
                    displayFilesCheckerForm(
                        [{ createdFiles: filteredFiles }],
                        getDosDocFileContent,
                    );
                } else {
                    enqueueSnackbar('Nouveaux documents non détectés', {
                        variant: 'info',
                        autoHideDuration: 2000,
                    });
                }
            }
        };

        const returnDocumentBasedOnLayout = () => {
            switch (currentLayout) {
                case LAYOUT_DOCUMENTS_FULL:
                    return (
                        <Box
                            sx={{
                                display: 'flex',
                                overflow: 'hidden',
                                width: '100%',
                            }}
                        >
                            <DossierDocumentsValidationList
                                displayList={listDisplayed}
                                currentLayout={currentLayout}
                            />
                            <Box sx={{ width: '70%', overflowY: 'hidden', maxHeight: '75vh' }}>
                                <DocumentViewer isResponsive />
                            </Box>
                        </Box>
                    );

                case LAYOUT_DOCUMENTS_1_3:
                    return (
                        <Box
                            sx={{
                                display: 'flex',
                                overflow: 'hidden',
                                width: '100%',
                            }}
                        >
                            <DossierDocumentsValidationList
                                displayList={listDisplayed}
                                currentLayout={currentLayout}
                            />
                            <Box
                                sx={{
                                    width: `${listDisplayed ? '55%' : '100%'}`,
                                    overflowY: 'hidden',
                                    maxHeight: '75vh',
                                }}
                            >
                                <DocumentViewer isResponsive />
                            </Box>
                        </Box>
                    );

                default:
                    return (
                        <Box
                            sx={{
                                display: 'flex',
                                overflow: 'hidden',
                                width: '100%',
                            }}
                        >
                            <DossierDocumentsValidationList
                                displayList={listDisplayed}
                                currentLayout={currentLayout}
                            />
                            <Box
                                sx={{
                                    width: `${listDisplayed ? '55%' : '100%'}`,
                                    overflowY: 'hidden',
                                    maxHeight: '75vh',
                                }}
                            >
                                <DocumentViewer isResponsive />
                            </Box>
                        </Box>
                    );
            }
        };

        return (
            <Box sx={wrapperSx}>
                <DossierDocumentsValidationHeader
                    onDocumentTypeChange={handleChangeDocumentType}
                    onDocumentStatusChange={onDocumentStatusChange}
                    onDocumentDeliassage={handleDeliassage}
                    onDocumentRefresh={refreshDossierDocumentsContext}
                    navigationBtn={navigationBtn}
                    isSubmitting={isSubmitting}
                />
                {returnDocumentBasedOnLayout()}
            </Box>
        );
    };

export default DossierDocumentsValidation;
